import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { partirTexto } from '../helpers/numerosNoTexto';

// Componentes de chat compartilhados entre a conversa 1:1 (Atendimentos) e os
// Grupos — pra manter paridade de comportamento numa fonte só.

// De onde o menu do toque longo se pendura. Ancorar nas pontas da bolha parece
// certo até aparecer uma mensagem comprida: aí o topo dela está a meia tela de
// distância do dedo, e o menu abre longe de onde a pessoa tocou. Então a âncora
// é uma faixa curta em volta do DEDO, presa dentro da bolha.
export function faixaDoToque(elemento, toqueY) {
  const r = elemento.getBoundingClientRect();
  const y = Math.min(Math.max(toqueY, r.top), r.bottom);
  return { top: Math.max(r.top, y - 24), bottom: Math.min(r.bottom, y + 24) };
}

export function formatNumero(numero) {
  const n = String(numero || '').replace(/\D/g, '');
  if (!n) return '';
  const local = n.startsWith('55') ? n.slice(2) : n;
  if (local.length === 11) return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return numero;
}

// A linha que aparece de cara — as mesmas seis do WhatsApp, na mesma ordem.
// É o PADRÃO: quem nunca reagiu vê exatamente isto. A partir do primeiro uso a
// régua vira a da pessoa (`useEmojisRapidos`).
export const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

// --- Régua de reação personalizada -----------------------------------------
//
// O backend conta cada emoji usado (fio_usuario_emoji) e devolve os mais
// frequentes em GET /preferencias. Aqui eles entram na frente da régua e o
// padrão preenche o que sobra — ninguém fica com régua vazia nem perde o 👍.
//
// O estado é de MÓDULO, não de componente: a mesma régua aparece em cinco
// lugares (conversa e grupo, no dedo e no mouse) e cada um montando a sua daria
// cinco requisições e cinco listas diferentes na mesma tela.
const QUANTOS_RAPIDOS = 6;
let rapidosCache = null;
let rapidosBuscando = null;
const ouvintesRapidos = new Set();

function misturarRapidos(preferidos) {
  const vistos = new Set();
  const fora = [];
  [...(preferidos || []), ...QUICK_EMOJIS].forEach((e) => {
    if (e && !vistos.has(e)) { vistos.add(e); fora.push(e); }
  });
  return fora.slice(0, QUANTOS_RAPIDOS);
}

function avisarOuvintes() { ouvintesRapidos.forEach((fn) => fn(rapidosCache)); }

// Uso registrado na hora, sem esperar o servidor: quem acabou de usar um emoji
// costuma usar de novo, e a régua já abre com ele na frente. O servidor conta
// em paralelo e na próxima carga a ordem dele prevalece.
export function anotarUsoEmoji(emoji) {
  if (!emoji) return;
  rapidosCache = misturarRapidos([emoji, ...(rapidosCache || QUICK_EMOJIS)]);
  avisarOuvintes();
}

// Tirar a reação (tocar na que já estava) não conta como uso: senão desmarcar
// empurraria o emoji pra frente da régua, que é o contrário do que a pessoa fez.
export function reagirEAnotar(onReagir, mensagem, emoji) {
  const tirando = mensagem.reacao === emoji;
  onReagir(mensagem, tirando ? '' : emoji);
  if (!tirando) anotarUsoEmoji(emoji);
}

export function useEmojisRapidos() {
  const [lista, setLista] = useState(rapidosCache || QUICK_EMOJIS);
  useEffect(() => {
    ouvintesRapidos.add(setLista);
    if (rapidosCache) setLista(rapidosCache);
    else if (!rapidosBuscando) {
      // Falha aqui não vira erro na tela: sem preferidos a régua é a padrão,
      // que é o que existia antes de tudo isto.
      rapidosBuscando = axios.get(`${API}/preferencias`)
        .then((r) => {
          rapidosCache = misturarRapidos(r.data?.data?.emojis_preferidos);
          avisarOuvintes();
        })
        .catch(() => { rapidosCache = QUICK_EMOJIS; avisarOuvintes(); });
    }
    return () => { ouvintesRapidos.delete(setLista); };
  }, []);
  return lista;
}

// O resto, atrás do "+": TODOS os emojis, não uma lista escolhida a dedo.
//
// A lista não vem no pacote — o próprio navegador sabe quais caracteres são
// emoji (a propriedade Unicode Emoji_Presentation marca os que já se desenham
// como emoji, sem precisar de seletor). Varremos as faixas uma vez e guardamos:
// custa ~1.200 testes de regex na primeira abertura, zero KB de download, e
// acompanha sozinho quando o navegador ganha emoji novo.
//
// Fora ficam as sequências compostas (família, profissões, tons de pele): são
// vários code points colados, e montá-las exigiria a tabela oficial do Unicode
// — que aí sim pesaria no pacote. Pra REAGIR, o conjunto simples cobre o uso.
const GRUPOS_UNICODE = [
  { titulo: 'Carinhas', faixas: [[0x1F600, 0x1F64F], [0x1F910, 0x1F97A]] },
  { titulo: 'Mãos e gente', faixas: [[0x1F440, 0x1F4AA], [0x1F90C, 0x1F90F], [0x1F9B0, 0x1F9DF]] },
  // A ORDEM importa: o primeiro grupo que reivindica um emoji fica com ele.
  // Comida vem antes de natureza porque a faixa dela mora dentro da outra —
  // invertido, "Comida" ficava com cinco itens e o resto ia pro balaio errado.
  { titulo: 'Comida', faixas: [[0x1F32D, 0x1F37F], [0x1F950, 0x1F97F]] },
  { titulo: 'Bichos e natureza', faixas: [[0x1F300, 0x1F43F], [0x1F980, 0x1F9AE]] },
  { titulo: 'Trabalho e objetos', faixas: [[0x1F4AB, 0x1F5FF], [0x1F9F0, 0x1FA9F]] },
  { titulo: 'Transporte e lugares', faixas: [[0x1F680, 0x1F6FF], [0x1F3D4, 0x1F3F0]] },
  { titulo: 'Símbolos', faixas: [[0x2600, 0x27BF], [0x2B00, 0x2BFF], [0x1F000, 0x1F2FF]] },
];

let cacheEmojis = null;

export function emojisPorGrupo() {
  if (cacheEmojis) return cacheEmojis;
  const vistos = new Set();
  cacheEmojis = GRUPOS_UNICODE.map(({ titulo, faixas }) => {
    const itens = [];
    faixas.forEach(([iniF, fimF]) => {
      for (let cp = iniF; cp <= fimF; cp += 1) {
        const c = String.fromCodePoint(cp);
        // `vistos` porque as faixas se sobrepõem de propósito (agrupar por tema
        // não bate com a ordem do Unicode), e emoji repetido na tela confunde.
        if (!vistos.has(c) && /\p{Emoji_Presentation}/u.test(c)) {
          vistos.add(c);
          itens.push(c);
        }
      }
    });
    return { titulo, itens };
  }).filter((g) => g.itens.length);
  return cacheEmojis;
}

// Texto da mensagem com o que é tocável virando toque: link abre, telefone
// oferece conversar. Fora isso o texto sai exatamente como veio.
//
// Antes NADA era tocável — nem link. Cliente mandava o endereço da obra no
// Google Maps e o atendente tinha que copiar na mão.
//
// `onTelefone` ausente = número fica texto comum. É o que os Grupos usam: lá
// "conversar com o número" significaria sair do grupo pra outra tela, e meia
// funcionalidade é pior que nenhuma.
export function TextoDaMensagem({ texto, ddd, ignorar, onTelefone, corLink }) {
  const pedacos = React.useMemo(
    () => partirTexto(texto, { ddd, ignorar, comTelefone: Boolean(onTelefone) }),
    [texto, ddd, ignorar, onTelefone],
  );
  if (!pedacos.length) return null;
  const cor = corLink || 'inherit';
  return (
    <>
      {pedacos.map((p, i) => {
        if (p.tipo === 'link') {
          const href = p.valor.startsWith('http') ? p.valor : `https://${p.valor}`;
          return (
            <a
              key={`l${i}`}
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              onClick={(e) => e.stopPropagation()}
              style={{ color: cor, textDecoration: 'underline' }}
            >{p.valor}</a>
          );
        }
        if (p.tipo === 'telefone') {
          return (
            <button
              key={`t${i}`}
              type="button"
              onClick={(e) => { e.stopPropagation(); onTelefone(p.numero, p.valor); }}
              // Herda tudo do texto em volta: um telefone no meio da frase que
              // muda de tamanho ou de fonte quebra a linha e chama atenção
              // demais. Sublinhado basta pra dizer que dá pra tocar.
              style={{
                background: 'none', border: 'none', padding: 0, margin: 0,
                font: 'inherit', color: cor, textDecoration: 'underline',
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >{p.valor}</button>
          );
        }
        return <React.Fragment key={`x${i}`}>{p.valor}</React.Fragment>;
      })}
    </>
  );
}

export const acaoBolhaStyle = {
  background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--portal-text)',
  fontSize: '0.9rem', lineHeight: 1, padding: '4px 5px', borderRadius: 6,
};
export const anexoItemStyle = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
  padding: '10px 14px', background: 'transparent', border: 'none', cursor: 'pointer',
  color: 'var(--portal-text)', fontSize: '0.9rem',
};

// Bloco de citação (reply) — mostra remetente + preview da mensagem citada.
export function QuotedPreviewBox({ autor, preview, onIr }) {
  // Tocar na citação leva até a original, como no WhatsApp. Sem `onIr` (quando
  // só temos o preview do webhook, sem a mensagem linkada) segue só decorativo.
  return (
    <div
      onClick={onIr ? (e) => { e.stopPropagation(); onIr(); } : undefined}
      role={onIr ? 'button' : undefined}
      title={onIr ? 'Ir até a mensagem citada' : undefined}
      style={{
        borderLeft: '3px solid rgba(255,255,255,0.45)', padding: '3px 8px', marginBottom: 6,
        borderRadius: '0 4px 4px 0', background: 'rgba(0,0,0,0.18)', fontSize: '0.78rem',
        cursor: onIr ? 'pointer' : 'default',
      }}
    >
      {autor && <div style={{ fontWeight: 700, opacity: 0.9, marginBottom: 1 }}>{autor}</div>}
      <div style={{ opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preview}</div>
    </div>
  );
}

// Cartão de contato (vCard) — estilo WhatsApp.
export function ContatoCard({ conteudo, out }) {
  let d = {};
  try { d = JSON.parse(conteudo || '{}'); } catch (_) { d = {}; }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 0', minWidth: 170 }}>
      <div style={{
        width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
        background: out ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <i className="bi bi-person-fill" style={{ fontSize: '1.2rem' }} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{d.fullName || 'Contato'}</div>
        {d.phoneNumber && <div style={{ fontSize: '0.8rem', opacity: 0.85 }}>{formatNumero(d.phoneNumber)}</div>}
      </div>
    </div>
  );
}

// Cartão de localização — abre no Google Maps.
export function LocalizacaoCard({ conteudo, out }) {
  let d = {};
  try { d = JSON.parse(conteudo || '{}'); } catch (_) { d = {}; }
  const url = `https://www.google.com/maps?q=${d.latitude},${d.longitude}`;
  return (
    <a href={url} target="_blank" rel="noreferrer" style={{ display: 'block', textDecoration: 'none', color: 'inherit', minWidth: 180 }}>
      <div style={{
        height: 88, borderRadius: 8, marginBottom: 4,
        background: out ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <i className="bi bi-geo-alt-fill" style={{ fontSize: '1.8rem' }} />
      </div>
      <div style={{ fontWeight: 600 }}>{d.name || 'Localização'}</div>
      {d.address && <div style={{ fontSize: '0.8rem', opacity: 0.85 }}>{d.address}</div>}
      <div style={{ fontSize: '0.78rem', opacity: 0.9, marginTop: 2 }}>
        <i className="bi bi-box-arrow-up-right me-1" />Abrir no mapa
      </div>
    </a>
  );
}

// Seletor de contato da agenda (pra enviar um contato).
export function ContatoPickerModal({ aberto, onClose, onEscolher }) {
  const [busca, setBusca] = useState('');
  const [contatos, setContatos] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!aberto) return;
    setBusca('');
    setLoading(true);
    axios.get(`${API}/contatos`).then((r) => setContatos(r.data?.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [aberto]);
  const filtrados = useMemo(() => {
    if (!busca) return contatos;
    const q = busca.toLowerCase();
    return contatos.filter((c) => String(c.push_name || '').toLowerCase().includes(q) || String(c.numero || '').includes(q));
  }, [contatos, busca]);
  if (!aberto) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div className="content-card" onClick={(e) => e.stopPropagation()} style={{ width: 'min(440px, 96vw)', height: 'min(78vh, 560px)', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
        <header style={{ padding: '12px 14px', borderBottom: '1px solid var(--portal-border)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <i className="bi bi-person-lines-fill" style={{ color: '#3D8B37' }} />
          <strong style={{ flex: 1 }}>Enviar contato</strong>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}><i className="bi bi-x-lg" /></button>
        </header>
        <div style={{ padding: '10px 14px', flexShrink: 0 }}>
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar na agenda..." autoFocus
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--portal-border)', background: 'var(--portal-surface)', color: 'var(--portal-text)', fontSize: '0.9rem' }} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 16, color: 'var(--portal-muted)', fontSize: '0.85rem' }}>Carregando...</div>
          ) : filtrados.length === 0 ? (
            <div style={{ padding: 16, color: 'var(--portal-muted)', fontSize: '0.85rem' }}>Nenhum contato encontrado.</div>
          ) : filtrados.map((c) => (
            <button key={c.id} type="button" onClick={() => onEscolher(c)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '10px 14px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--portal-border)', cursor: 'pointer', color: 'var(--portal-text)', textAlign: 'left' }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(61,139,55,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-person-fill" style={{ color: '#3D8B37' }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.push_name || formatNumero(c.numero)}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--portal-muted)' }}>{formatNumero(c.numero)}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Menu de ações no MOBILE/TABLET: overlay full-screen igual WhatsApp.
// Menu do toque longo. Ele nasce COLADO na mensagem tocada, não no meio da
// tela: centralizado, quem tocou perde a referência do que está prestes a
// apagar. `ancora` é o retângulo da bolha no momento do toque.
//
// Arranjo preferido é o do WhatsApp: reações em cima da bolha, ações embaixo,
// com a mensagem visível no meio. Perto das bordas isso não cabe, e aí os dois
// vão juntos pro lado que sobrou — melhor empilhar que sair da tela.
// `recalcular`: qualquer coisa que mude a ALTURA do que está sendo posicionado
// (abrir "mais reações", por exemplo) precisa refazer a conta, senão o menu
// fica no lugar do tamanho antigo e cobre a mensagem.
export function usePosicaoAcoes(mensagem, ancora, recalcular) {
  const reacoesRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!mensagem) { setPos(null); return; }
    const hR = reacoesRef.current?.offsetHeight || 56;
    const hM = menuRef.current?.offsetHeight || 190;
    const vh = window.innerHeight;
    const M = 10;   // respiro da borda da tela
    const G = 10;   // folga entre a bolha e o que aparece
    const E = 8;    // folga entre reações e menu quando ficam juntos

    if (!ancora) {
      const total = hR + E + hM;
      const topo = Math.max(M, (vh - total) / 2);
      setPos({ reacoes: topo, menu: topo + hR + E });
      return;
    }

    const cabeAcima = ancora.top - G - hR >= M;
    const cabeAbaixo = ancora.bottom + G + hM <= vh - M;
    let reacoes;
    let menu;

    if (cabeAcima && cabeAbaixo) {
      reacoes = ancora.top - G - hR;
      menu = ancora.bottom + G;
    } else if (cabeAbaixo) {
      reacoes = ancora.bottom + G;
      menu = reacoes + hR + E;
      if (menu + hM > vh - M) {
        menu = vh - M - hM;
        reacoes = Math.max(M, menu - E - hR);
      }
    } else {
      menu = ancora.top - G - hM;
      reacoes = menu - E - hR;
      if (reacoes < M) { reacoes = M; menu = reacoes + hR + E; }
    }
    setPos({ reacoes, menu });
  }, [mensagem, ancora, recalcular]);

  // Enquanto não mediu, fica invisível: aparecer no lugar errado e pular depois
  // é pior do que aparecer um quadro mais tarde.
  const ancoragem = (topo, out) => ({
    position: 'fixed',
    top: pos ? Math.round(topo) : 0,
    ...(out ? { right: 16 } : { left: 16 }),
    visibility: pos ? 'visible' : 'hidden',
  });

  return { reacoesRef, menuRef, pos, ancoragem };
}

export function AcoesMensagemMobile({ mensagem, ancora, onFechar, onReagir, onResponder, onEditar, onApagar, onCopiar }) {
  const [maisAberto, setMaisAberto] = useState(false);
  const rapidos = useEmojisRapidos();
  const { reacoesRef, menuRef, pos, ancoragem } = usePosicaoAcoes(mensagem, ancora, maisAberto);

  if (!mensagem) return null;
  const out = mensagem.direcao === 'out';
  const item = (icon, label, onClick, danger) => (
    <button type="button" onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left',
      padding: '13px 18px', background: 'transparent', border: 'none', cursor: 'pointer',
      color: danger ? '#e5484d' : 'var(--portal-text)', fontSize: '1rem',
      borderBottom: '1px solid var(--portal-border)',
    }}><i className={`bi ${icon}`} style={{ fontSize: '1.1rem', width: 22 }} />{label}</button>
  );
  return (
    <div
      onClick={onFechar}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
      }}
    >
      {/* COLUNA, não linha: com a régua e o painel lado a lado num flex-wrap, o
          painel virava item de flex e itens de flex crescem em vez de rolar
          (min-height:auto). O `overflow` existia e não funcionava. */}
      <div ref={reacoesRef} onClick={(e) => e.stopPropagation()} style={{ ...ancoragem(pos?.reacoes ?? 0, out), display: 'flex', flexDirection: 'column', maxWidth: 'min(92vw, 340px)', background: 'var(--portal-surface)', borderRadius: maisAberto ? 14 : 28, padding: '8px 12px', boxShadow: '0 6px 20px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {rapidos.map((e) => (
          <button key={e} type="button" onClick={() => { reagirEAnotar(onReagir, mensagem, e); onFechar(); }}
            style={{ background: mensagem.reacao === e ? 'rgba(61,139,55,0.18)' : 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.7rem', lineHeight: 1, padding: '4px 6px', borderRadius: '50%' }}>{e}</button>
        ))}
        {/* Abre no lugar, sem trocar de tela: a mensagem continua atrás e a
            pessoa não perde de vista o que está reagindo. */}
        {!maisAberto && (
          <button
            type="button"
            onClick={() => setMaisAberto(true)}
            title="Todas as reações"
            aria-label="Todas as reações"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.4rem', lineHeight: 1, padding: '4px 8px', borderRadius: '50%', color: 'var(--portal-muted)' }}
          >
            +
          </button>
        )}
        </div>
        {/* Painel com TODOS: rola dentro de si, com teto de altura, pra não
            empurrar o menu de ações pra fora da tela num celular deitado.
            `minHeight: 0` é o que destrava a rolagem dentro de um flex. */}
        {maisAberto && (
          <div style={{
            width: 'min(88vw, 300px)', maxHeight: '40vh', minHeight: 0,
            overflowY: 'auto', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y',
            overscrollBehavior: 'contain',
            marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--portal-border)',
          }}>
            {emojisPorGrupo().map((g) => (
              <div key={g.titulo}>
                <div style={{
                  position: 'sticky', top: 0, background: 'var(--portal-surface)',
                  fontSize: '0.68rem', fontWeight: 700, color: 'var(--portal-muted)',
                  padding: '4px 2px', textTransform: 'uppercase', letterSpacing: 0.5,
                }}>{g.titulo}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {g.itens.map((e) => (
                    <button key={e} type="button"
                      onClick={() => { reagirEAnotar(onReagir, mensagem, e); onFechar(); }}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.5rem', lineHeight: 1.1, padding: '2px 4px', borderRadius: '50%' }}>{e}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div ref={menuRef} onClick={(e) => e.stopPropagation()} style={{ ...ancoragem(pos?.menu ?? 0, out), background: 'var(--portal-surface)', borderRadius: 14, overflow: 'hidden', minWidth: 220, boxShadow: '0 6px 20px rgba(0,0,0,0.35)' }}>
        {mensagem.tipo !== 'sistema' && onResponder && item('bi-reply-fill', 'Responder', () => { onResponder(mensagem); onFechar(); })}
        {(mensagem.conteudo && !mensagem.apagada) && onCopiar && item('bi-copy', 'Copiar', () => { onCopiar(mensagem); onFechar(); })}
        {out && mensagem.tipo === 'texto' && !mensagem.apagada && onEditar && item('bi-pencil', 'Editar', () => { onEditar(mensagem); onFechar(); })}
        {out && !mensagem.apagada && onApagar && item('bi-trash', 'Apagar', () => { onApagar(mensagem); onFechar(); }, true)}
      </div>
    </div>
  );
}
